<template>
  <div class="v-cal">
    <header class="v-cal-header">
      <div class="v-cal-header__title-bar">
        <div>{{ calendarTitle.date }}</div>
        <el-tooltip content="Zoom out" :open-delay="500" placement="left">
          <el-button class="zoom-button minus" size="small" @click="zoom(-600)" circle><font-awesome-icon icon='search-minus' /></el-button>
        </el-tooltip>
        <el-tooltip content="Zoom in" :open-delay="500" placement="left">
          <el-button class="zoom-button plus" size="small" @click="zoom(600)" circle><font-awesome-icon icon='search-plus' /></el-button>
        </el-tooltip>
        <el-tooltip content="Fit to screen" :open-delay="500" placement="left">
          <el-button class="zoom-button fit" size="small" @click="zoomFit()" circle><font-awesome-icon icon='expand' /></el-button>
        </el-tooltip>
        <div><span v-if="parseFloat(calendarTitle.total) > 0">{{ calendarTitle.total }}</span></div>
      </div>
    </header>
    
    <component
            :is="activeView"
            :class="'v-cal-content--' + activeView"
            v-bind="activeViewProps"
    ></component>
    
    <footer class="v-cal-footer"></footer>
  </div>
</template>

<script>

import Event from '../model/Event'
import { defaultLabels, defaultViews } from '../utils/config'
import { EventBus } from './EventBus'
import moment from 'moment'
import Week from './views/Week'
import EventDialog from './dialog'
import { mapTimeEntries } from '@/utils/timeEntry'
import 'moment/locale/fr'
import { EventHub } from '@/components/EventHub'

let config = {
  locale: 'en',
  showTodayButton: true,
  minDate: null,
  maxDate: null,
  labels: defaultLabels,
  timeRange: [0, 23],
  availableViews: defaultViews,
  initialDate: new Date(),
  initialView: 'week',
  use12: false,
  showTimeMarker: true,
  eventDisplay: null
}

export default {
  name: 'VueScheduler',
  components: { Week },
  props: {
    users: Array,
    events: {
      type: Array,
      default: () => []
    },
    showTodayButton: {
      type: Boolean,
      default: () => config.showTodayButton
    },
    minDate: {
      type: [Date, Object],
      default: () => config.minDate
    },
    maxDate: {
      type: [Date, Object],
      default: () => config.maxDate
    },
    labels: {
      type: Object,
      default: () => config.labels,
      validator (value) {
        for (const labelKey in defaultLabels) {
          if (!value.hasOwnProperty(labelKey)) {
            return false
          }
        }
        return true
      }
    },
    timeRange: {
      type: Array,
      default: () => config.timeRange,
      validator (value) {
        if (value.length !== 2 || value[0] > value[1] || value[0] < 0 || value[1] > 23) {
          return false
        }
        return true
      }
    },
    availableViews: {
      type: Array,
      default: () => config.availableViews,
      validator (value) {
        const possible = defaultViews
        let error = false
        value.forEach(view => {
          if (possible.indexOf(view) === -1) {
            error = true
          }
        })
        return !error
      }
    },
    initialDate: {
      type: [Date, Object],
      default: () => config.initialDate
    },
    initialView: {
      type: String,
      default: () => config.initialView
    },
    use12: {
      type: Boolean,
      default: () => config.use12
    },
    showTimeMarker: {
      type: Boolean,
      default: () => config.showTimeMarker
    },
    eventDisplay: {
      type: [String, Function],
      default: () => config.eventDisplay
    },
    onZoom: {
      type: [String, Function]
    },
    zoomFit: {
      type: [String, Function]
    },
    disableDialog: {
      type: Boolean,
      default: false
    },
    eventDialogConfig: {
      type: Object,
      default: () => { return {} }
    }
  },
  data () {
    return {
      today: moment(),
      activeView: '',
      activeDate: null,
      selectedEvent: null
    }
  },
  mounted () {
    //  Initial setup
    this.activeView = this.initialView
    this.activeDate = moment(this.initialDate)

    //  Bind events
    this.bindEvents()
  },
  beforeDestroy () {
    EventBus.$off('day-clicked')
    EventBus.$off('time-clicked')
    EventBus.$off('event-clicked')
  },
  methods: {
    zoom (e) {
      this.onZoom(e)
    },
    openEventDialog (data) {
      if (!this.disableDialog) {
        const { fields, ...config } = this.eventDialogConfig

        if (data instanceof Date) {
          config.date = data
        } else {
          config.date = data.date
          config.startTime = data.time !== null ? moment(data.time, 'HH') : null
          config.endTime = data.time !== null ? moment(data.time, 'HH').add(1, 'h') : null
        }

        EventDialog.show(config, fields)
          .$on('event-created', (event) => {
            this.events.push(event._e)
            this.$emit('event-created', event._e)
          })
      }
    },
    saveTimeEntry (entry) {
      if (!entry.id) {
        this.$store.dispatch('time-entries/create', entry).then(() => {
        })
      } else {
        this.$store.dispatch('time-entries/update', entry).then(() => { this.$store.dispatch('time-entries/loadById', { id: entry.id }) })
      }
    },
    bindEvents () {
      EventBus.$on('day-clicked', (date) => {
        this.$emit('day-clicked', date)
        this.openEventDialog(date)
      })
      EventBus.$on('time-clicked', (data) => {
        this.$emit('time-clicked', data)
        this.openEventDialog(data)
      })
      EventBus.$on('event-clicked', (event) => {
        let entry = this.$store.getters['time-entries/byId']({id: event.id})
        this.selectedEvent = entry

        let params = {
          entries: mapTimeEntries([entry]),
          title: 'Edit time entry',
          save: this.saveTimeEntry
        }

        EventHub.$emit('show-new-entry-dialog', params)
      })
    },
    goToToday () {
      this.activeDate = moment(this.today)
    },
    prev () {
      this.activeDate = moment(this.activeDate.subtract(1, this.activeView + 's'))
    },
    next () {
      this.activeDate = moment(this.activeDate.add(1, this.activeView + 's'))
    },
    switchView (view) {
      this.activeView = view
    }
  },
  filters: {
    capitalizeFirstLetter (string) {
      return string ? string.charAt(0).toUpperCase() + string.slice(1) : ''
    }
  },
  watch: {
    initialDate () {
      this.activeDate = moment(this.initialDate)
    },
    initialView () {
      this.activeView = this.initialView
    },
    activeDate () {
      this.$emit(this.activeView + '-changed', this.activeDate.toDate())
    },
    activeView () {
      this.$emit('view-changed', this.activeView)
    }
  },
  computed: {
    newEvents () {
      return this.events.map(e => {
        return new Event(e).bindGetter('displayText', this.eventDisplay)
      })
    },
    isPrevAllowed () {
      if (this.minDate) {
        const prevRef = moment(this.activeDate).subtract(1, this.activeView + 's')
        return this.minDate.isSameOrBefore(prevRef, this.activeView)
      }
      return true
    },
    isNextAllowed () {
      if (this.maxDate) {
        const afterRef = moment(this.activeDate).add(1, this.activeView + 's')
        return this.maxDate.isSameOrAfter(afterRef, this.activeView)
      }
      return true
    },
    activeViewProps () {
      let props = {
        activeDate: this.activeDate,
        minDate: this.minDate,
        maxDate: this.maxDate,
        use12: this.use12,
        events: this.newEvents,
        showTimeMarker: this.showTimeMarker,
        timeRange: this.timeRange,
        allDayLabel: this.labels.all_day
      }
      return props
    },
    calendarTitle () {
      if (this.activeDate === null) {
        return ''
      }

      let active = moment(this.activeDate)
      let first = moment(active).startOf('week')
      let last = moment(active).endOf('week')
      let isSameMonth = first.isSame(last, 'month')
      let format1 = `${active.format('MMMM')} ${first.format('D')} - ${last.format('D')}, ${active.format('YYYY')}`
      let format2 = `${first.format('MMM D')} - ${last.format('MMM D')}, ${active.format('YYYY')}`

      let sum = this.activeViewProps.events.reduce((sum, event) => sum + (!event.isPersonal ? parseFloat(event.totalTime) : 0), 0)

      return { date: isSameMonth ? format1 : format2, total: `${sum.toFixed(1)}h` }
    }
  }
}
</script>

<style scoped lang='scss'>
@import '@/styles/common.scss';
.calendar {
  .v-cal-header__title-bar {
    display: flex;
    align-items:stretch;

    div {
      flex-grow: 1;
      font-size: 18px;

      &:first-child {
        margin-left: 67px;
      }
      &:last-child {
        color: #409eff;
        text-align: right;
        margin-right: 10px;
      }
    }
  }
}
.zoom-button{
  
  right:35px;
  position:fixed;
  margin-top:-10px;
  z-index: 9999;
  &.plus{
    bottom: 70px;
  }
  &.minus{
    bottom:30px;
  }
  &.fit{
    bottom:110px;
  }
}
.calendar-duration{
 color: #409eff;
 font-weight:bold;
 padding:5px;
}
</style>


